import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { ODDS_FILTER_OPTIONS, ODDS_VIEW } from "../../../../constants";
import FilterSelection from "../FilterSelection";
import styles from "./FilterOdds.module.scss";
import FilterSelectionLoadingSkeleton from "../../../../components/FilterSelectionLoadingSkeleton";
import {
  removeOddsAndUpdateFilter,
  selectOddsAndUpdateFilter,
} from "../../../../actions/filters";
import { DESELECT, FILTERS, SELECT } from "../../../../constants/analytics";
import { sendAnalyticsEvent } from "../../../../lib/postMessage";

export default function FilterOdds() {
  const dispatch = useDispatch();
  const { format } = useSelector((state) => state.betslip);
  const { prefetchedFilters } = useSelector((state) => state.filter);
  const { odds } = prefetchedFilters;
  const { minOdds, maxOdds } = odds;
  const oddsFormatLower = format.toLowerCase();

  const isOddsRangeSelected = (option) => {
    if (minOdds === null && maxOdds === null) {
      return false;
    }
    if (option.minOdds >= minOdds && option.maxOdds <= maxOdds) {
      return true;
    }

    return false;
  };

  const handleFilterClick = (option) => {
    const dispatchOddsUpdate = (minOdds, maxOdds) => {
      dispatch(
        selectOddsAndUpdateFilter({
          minOdds,
          maxOdds,
          isDecimal: true,
        })
      );
    };

    const findOptionIndex = (predicate) =>
      ODDS_FILTER_OPTIONS.findIndex(predicate);

    const getOptionByIndex = (index) => ODDS_FILTER_OPTIONS[index];
    // No current selection - select the clicked option
    if (minOdds === null && maxOdds === null) {
      sendAnalyticsEvent({
        event: FILTERS,
        type: ODDS_VIEW,
        action: SELECT,
        minValue: option.minOdds === null ? "Min" : option.minOdds,
        maxValue: option.maxOdds === Infinity ? "Max" : option.maxOdds,
      });
      dispatchOddsUpdate(option.minOdds, option.maxOdds);
      return;
    }

    // Option is already selected
    if (isOddsRangeSelected(option)) {
      sendAnalyticsEvent({
        event: FILTERS,
        type: ODDS_VIEW,
        action: DESELECT,
        minValue: option.minOdds === null ? "Min" : option.minOdds,
        maxValue: option.maxOdds === Infinity ? "Max" : option.maxOdds,
      });
      // Single range selected - remove selection
      if (minOdds === option.minOdds && maxOdds === option.maxOdds) {
        dispatch(removeOddsAndUpdateFilter());
        return;
      }

      // Multiple ranges selected - handle edge removals
      const removeMinEdge = minOdds === option.minOdds;
      const removeMaxEdge = maxOdds === option.maxOdds;
      const removeFromMiddle =
        minOdds < option.minOdds && maxOdds > option.maxOdds;

      if (removeMinEdge) {
        const minIndex = findOptionIndex((o) => o.minOdds === minOdds);
        const nextOption = getOptionByIndex(minIndex + 1);
        dispatchOddsUpdate(nextOption.minOdds, maxOdds);
        return;
      }

      if (removeMaxEdge) {
        const maxIndex = findOptionIndex((o) => o.maxOdds === maxOdds);
        const prevOption = getOptionByIndex(maxIndex - 1);
        dispatchOddsUpdate(minOdds, prevOption.maxOdds);
        return;
      }

      if (removeFromMiddle) {
        const currentIndex = findOptionIndex(
          (o) => o.minOdds === option.minOdds || o.maxOdds === option.maxOdds
        );

        // removing second-to-last option when last option is Infinity
        // to ensure there is always a continuous range
        const isSecondToLast = currentIndex === ODDS_FILTER_OPTIONS.length - 2;
        const isLastOptionInfinity =
          odds.maxOdds === Infinity && odds.minOdds === null;

        if (isSecondToLast && isLastOptionInfinity) {
          const prevOption = getOptionByIndex(currentIndex - 1);
          dispatchOddsUpdate(minOdds, prevOption.maxOdds);
          return;
        }

        const nextOption = getOptionByIndex(currentIndex + 1);
        dispatchOddsUpdate(nextOption.minOdds, maxOdds);
        return;
      }
    }

    // Option is not selected - expand range to include it
    const newMinOdds = option.minOdds < minOdds ? option.minOdds : minOdds;
    const newMaxOdds = option.maxOdds > maxOdds ? option.maxOdds : maxOdds;

    sendAnalyticsEvent({
      event: FILTERS,
      type: ODDS_VIEW,
      action: SELECT,
      minValue: option.minOdds === null ? "Min" : option.minOdds,
      maxValue: option.maxOdds === Infinity ? "Max" : option.maxOdds,
    });

    dispatchOddsUpdate(newMinOdds, newMaxOdds);
  };

  return (
    <div className={styles.filterOdds}>
      {ODDS_FILTER_OPTIONS.map((option) => {
        return (
          <React.Fragment key={option[oddsFormatLower].label}>
            <FilterSelection
              showCheckbox
              checked={isOddsRangeSelected(option)}
              onClick={() => handleFilterClick(option)}
              label={option[oddsFormatLower].label}
            >
              <FilterSelectionLoadingSkeleton
                skeletonKey={`${option[oddsFormatLower].label}`}
                textBlocks={1}
              />
              <h4>{option[oddsFormatLower].label}</h4>
            </FilterSelection>
          </React.Fragment>
        );
      })}
    </div>
  );
}
